'use client';

import { useTranslations } from 'next-intl';
import { CustomImage as Image } from 'components/shared/custom-image';
import { ComparisonProps } from './types';
import { useRef } from 'react';
import styles from './DesktopTableNew.module.css';

type DesktopProps = Array<ComparisonProps>;

export const DesktopTableNew = ({
  className,
  data,
  comparatorsOrder,
  uhRingPrice,
}: {
  className?: string;
  data: DesktopProps;
  comparatorsOrder: number[];
  uhRingPrice: string;
}) => {
  const t = useTranslations('ring');
  const deskTableContainer = useRef<HTMLTableElement>(null);

  return (
    <table
      className={[styles.root, className].filter(Boolean).join(' ')}
      ref={deskTableContainer}
    >
        <tbody>
          {/* Form Section Header */}
          <tr>
            <th
              className="property property-head"
              scope="col"
              colSpan={comparatorsOrder.length}
            >
              <span className="">{t('ring.comparison.feature.form.head')}</span>
            </th>
          </tr>

          {/* Weight Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.form.categoryA')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/uh_weight.svg"
                    width={52}
                    height={52}
                    alt="weight-icon"
                    className="weight-icon"
                  />
                </div>
                <span className="bold-text">
                  {t('ring.comparison.uhRing.form.weight.bold')}
                </span>
                <span className="sub-bold-text">
                  {t('ring.comparison.uhRing.form.weight.sub')}
                </span>
                <span className="thin-text">
                  {t('ring.comparison.uhRing.form.weight.thin')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            <Image
                              src={
                                data[id].weightIcon
                                  ? (data[id].weightIcon as string)
                                  : ''
                              }
                              width={id == 0 ? 46 : 52}
                              height={id == 0 ? 46 : 52}
                              alt="weight-icon"
                              className="weight-icon"
                            />
                          </div>
                          <span className="bold-text">
                            {data[id].weight ? data[id].weight : ''}
                          </span>
                          <span className="sub-bold-text">
                            {data[id].weightSub ? data[id].weightSub : ''}
                          </span>
                          <span className="thin-text">
                            {data[id].weightThin ? data[id].weightThin : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Form Factor Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.form.categoryB')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/formFator_ring.svg"
                    width={52}
                    height={52}
                    alt="ring-icon"
                    className="ring-icon"
                  />
                </div>
                <span className="bold-text">
                  {t('ring.comparison.uhRing.form.formFactor.a')}
                  <br />
                  {t('ring.comparison.uhRing.form.formFactor.b')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            <Image
                              src={
                                data[id].formFactorIcon
                                  ? (data[id].formFactorIcon as string)
                                  : ''
                              }
                              width={52}
                              height={52}
                              alt="form-factor-icon"
                              className="form-factor-icon"
                            />
                          </div>
                          <span className="bold-text">
                            {data[id].formFactor ? data[id].formFactor : ''}
                            <br />
                            {data[id].formFactorDisplay
                              ? data[id].formFactorDisplay
                              : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Break Row */}
          <tr>
            <td className="col-span" colSpan={comparatorsOrder.length + 1}>
              <div className="break" />
            </td>
          </tr>

          {/* Real-Time Metrics Section Header */}
          <tr>
            <th
              className="property property-head"
              scope="col"
              colSpan={comparatorsOrder.length}
            >
              <span className="">
                {t('ring.comparison.feature.realtime.head.a')}{' '}
                {t('ring.comparison.feature.realtime.head.b')}
              </span>
            </th>
          </tr>

          {/* Heart Rate Monitoring Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.realtime.categoryA.a')}
                <br />
                {t('ring.comparison.feature.realtime.categoryA.b')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">
                  {t(
                    'ring.comparison.uhRing.realTimeMetrics.hrMonitoring.bold',
                  )}
                </span>
                <span className="thin-text">
                  {t(
                    'ring.comparison.uhRing.realTimeMetrics.hrMonitoring.thin',
                  )}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].heartRateMonitoring
                              ? data[id].heartRateMonitoring
                              : ''}
                          </span>
                          <span className="thin-text">
                            {data[id].heartRateMonitoringSub
                              ? data[id].heartRateMonitoringSub
                              : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* HRV Monitoring Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.realtime.categoryB')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">
                  {t(
                    'ring.comparison.uhRing.realTimeMetrics.hrvMonitoring.bold',
                  )}
                </span>
                <span className="thin-text">
                  {t(
                    'ring.comparison.uhRing.realTimeMetrics.hrvMonitoring.thin',
                  )}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].hrvMonitoringRtm
                              ? data[id].hrvMonitoringRtm
                              : ''}
                          </span>
                          <span className="thin-text">
                            {data[id].hrvMonitoringRtmSub
                              ? data[id].hrvMonitoringRtmSub
                              : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Skin Temperature Tracking Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.realtime.categoryC.a')}
                <br />
                {t('ring.comparison.feature.realtime.categoryC.b')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">
                  {t('ring.comparison.uhRing.realTimeMetrics.skinTemp.bold')}
                </span>
                <span className="thin-text">
                  {t('ring.comparison.uhRing.realTimeMetrics.skinTemp.thin')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].skinTempTracking
                              ? data[id].skinTempTracking
                              : ''}
                          </span>
                          <span className="thin-text">
                            {data[id].skinTempTrackingSub
                              ? data[id].skinTempTrackingSub
                              : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Break Row */}
          <tr>
            <td className="col-span" colSpan={comparatorsOrder.length + 1}>
              <div className="break" />
            </td>
          </tr>

          {/* Sleep Section Header */}
          <tr>
            <th
              className="property property-head"
              scope="col"
              colSpan={comparatorsOrder.length}
            >
              <span className="">
                {t('ring.comparison.feature.sleep.head')}
              </span>
            </th>
          </tr>

          {/* Sleep Tracking Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.sleep.categoryA')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].sleepTracking ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Sleep Metrics Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.sleep.categoryB')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">
                  {t('ring.comparison.uhRing.sleep.sleepMetrics')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].sleepMetrics ? data[id].sleepMetrics : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Break Row */}
          <tr>
            <td className="col-span" colSpan={comparatorsOrder.length + 1}>
              <div className="break" />
            </td>
          </tr>

          {/* Movement & Workout Section Header */}
          <tr>
            <th
              className="property property-head"
              scope="col"
              colSpan={comparatorsOrder.length}
            >
              <span className="">
                {t('ring.comparison.feature.movementWorkout.head')}
              </span>
            </th>
          </tr>

          {/* Step Counting Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.movementWorkout.categoryA')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].stepCounting ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Distance and Calories Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.movementWorkout.categoryB')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].distance ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* VO2 Max Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.movementWorkout.categoryC')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].vo2Max ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Break Row */}
          <tr>
            <td className="col-span" colSpan={comparatorsOrder.length + 1}>
              <div className="break" />
            </td>
          </tr>

          {/* Recovery Section Header */}
          <tr>
            <th
              className="property property-head"
              scope="col"
              colSpan={comparatorsOrder.length}
            >
              <span className="">
                {t('ring.comparison.feature.recovery.head')}
              </span>
            </th>
          </tr>

          {/* HRV Metrics Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.recovery.categoryA')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">
                  {t('ring.comparison.uhRing.recovery.hrvMetrics')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].hrvMetrics ? data[id].hrvMetrics : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Stress Monitoring Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.recovery.categoryC')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].stressMonitoring ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Break Row */}
          <tr>
            <td className="col-span" colSpan={comparatorsOrder.length + 1}>
              <div className="break" />
            </td>
          </tr>

          {/* Function Section Header */}
          <tr>
            <th
              className="property property-head"
              scope="col"
              colSpan={comparatorsOrder.length}
            >
              <span className="">
                {t('ring.comparison.feature.function.head')}
              </span>
            </th>
          </tr>

          {/* Battery Life Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.function.categoryA')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/uh_battery.svg"
                    width={52}
                    height={24.9}
                    alt="uh-battery-icon"
                    className="uh-battery-icon"
                  />
                </div>
                <span className="bold-text">
                  {t('ring.comparison.uhRing.function.batteryLife.bold')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            <Image
                              src={
                                data[id].batteryLifeIcon
                                  ? (data[id].batteryLifeIcon as string)
                                  : ''
                              }
                              width={52}
                              height={24.9}
                              alt="battery-icon"
                              className="battery-icon"
                            />
                          </div>
                          <span className="bold-text">
                            {data[id].batteryLife ? data[id].batteryLife : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Full Charging Time Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.function.categoryB.a')}
                <br />
                {t('ring.comparison.feature.function.categoryB.b')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">
                  {t('ring.comparison.uhRing.function.fullChargingTime.bold')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].fullChargingTime
                              ? data[id].fullChargingTime
                              : ''}{' '}
                          </span>
                          <span className="thin-text">
                            {data[id].fullChargingSub
                              ? data[id].fullChargingSub
                              : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Water Proof Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.function.categoryC')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">
                  {t('ring.comparison.uhRing.function.waterProof.bold')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].waterproof ? data[id].waterproof : ''}{' '}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* GPS Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.function.categoryD')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/crossmark_new.svg"
                    width={28}
                    height={31}
                    alt="cross-icon"
                    className="cross-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].gps ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Airplane Mode Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.function.categoryE')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].airplaneMode ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Find My Device Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.function.categoryF')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].findMyDevice ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Real-time Actionable Nudges Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.function.categoryH.a')}
                <br />
                {t('ring.comparison.feature.function.categoryH.b')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].realTimeNotifications ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Break row for Unique Value Proposition */}
          <tr className="col-span">
            <td colSpan={comparatorsOrder.length + 1}>
              <div className="break"></div>
            </td>
          </tr>

          {/* Unique Value Proposition Section Header */}
          <tr>
            <th
              className="property property-head"
              scope="col"
              colSpan={comparatorsOrder.length}
            >
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.head')}
              </span>
            </th>
          </tr>

          {/* Form Factor Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.categoryA')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/formFator_ring.svg"
                    width={42}
                    height={42}
                    alt="ring-icon"
                    className="ring-icon"
                  />
                </div>
                <span className="bold-text">
                  {t('ring.comparison.uhRing.form.formFactor.a')}
                  <br />
                  {t('ring.comparison.uhRing.form.formFactor.b')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            <Image
                              src={
                                data[id].formFactorIcon
                                  ? (data[id].formFactorIcon as string)
                                  : ''
                              }
                              width={42}
                              height={42}
                              alt="form-factor-icon"
                              className="form-factor-icon"
                            />
                          </div>
                          <span className="bold-text">
                            {data[id].formFactor ? data[id].formFactor : ''}
                            <br />
                            {data[id].formFactorDisplay
                              ? data[id].formFactorDisplay
                              : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Circadian Rhythm Tracking Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.categoryB')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].circadianRhythmTracking ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/*Temp Based Ovulation Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.categoryG')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].tempBasedOvulation ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Caffeine Window Detection Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.categoryC')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].caffeineWindowDetection ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Afib Detection Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.categoryD')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
                <span className="sub-bold-text">
                  {t('desktopTableNew.text.limitedRegions')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].afibDetection ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Mandatory Data Subscription Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.categoryE')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">
                  {t('desktopTableNew.text.required')}
                </span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].subscriptionRequired
                              ? data[id].subscriptionRequired
                              : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Performance Coach Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.uniqueValProp.categoryF')}
              </span>
            </th>
            <td>
              <div className="content">
                <div className="img-container">
                  <Image
                    src="web_v2/ring-buy/comparison/checkmark_new.svg"
                    width={30}
                    height={31}
                    alt="checkmark-icon"
                    className="checkmark-icon"
                  />
                </div>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <div className="img-container">
                            {data[id].performanceCoach ? (
                              <Image
                                src="web_v2/ring-buy/comparison/checkmark_new.svg"
                                width={30}
                                height={31}
                                alt="checkmark-icon"
                                className="checkmark-icon"
                              />
                            ) : (
                              <Image
                                src="web_v2/ring-buy/comparison/crossmark_new.svg"
                                width={28}
                                height={31}
                                alt="cross-icon"
                                className="cross-icon"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>

          {/* Break row for Pricing */}
          <tr className="col-span">
            <td colSpan={comparatorsOrder.length + 1}>
              <div className="break"></div>
            </td>
          </tr>

          {/* Pricing Section Header */}
          <tr>
            <th
              className="property property-head"
              scope="col"
              colSpan={comparatorsOrder.length}
            >
              <span className="">
                {t('ring.comparison.feature.pricing.head')}
              </span>
            </th>
          </tr>

          {/* Price Range Row */}
          <tr>
            <th className="property" scope="row">
              <span className="">
                {t('ring.comparison.feature.pricing.categoryA')}
              </span>
            </th>
            <td>
              <div className="content">
                <span className="bold-text">{uhRingPrice}</span>
              </div>
            </td>
            {data && comparatorsOrder
              ? comparatorsOrder.map((id, idx) => {
                  if (idx !== comparatorsOrder.length - 1) {
                    return (
                      <td key={idx} className="desk">
                        <div className="content">
                          <span className="bold-text">
                            {data[id].priceRange ? data[id].priceRange : ''}
                          </span>
                        </div>
                      </td>
                    );
                  }
                })
              : null}
          </tr>
        </tbody>
      </table>
    );
};
